import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { compose, isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { loadMore, loadPage, reload } from 'features/views/contacts/actions';
import { getContacts } from 'features/views/contacts/selectors';
import contactTake from 'features/views/contactTake';

import ContactsRow from './TableRow';

const fetchStorePath = `${fetch.constants.urls.contactList}.take`;

export const ContactsTable = ({ solutionStates, idkRegionsCodelist, idkAgenciesCodelist }) => {
	const dispatch = useDispatch();

	const { totalRows } = useSelector(fetch.selectors.getMetaData(fetchStorePath));
	const onlyOneRecord = totalRows === 1;

	const fetchState = useSelector(fetch.selectors.getFetchState(fetchStorePath));
	const selectedContactId = useSelector(contactTake.selectors.getSelectedContactId);
	const contacts = useSelector(getContacts('take')) || EMPTY_VALUES.ARRAY;

	useEffect(() => {
		if (!selectedContactId && contacts.length === 1 && onlyOneRecord) {
			dispatch(contactTake.actions.selectContact(contacts[0].id));
		}
	}, [contacts, dispatch, selectedContactId, onlyOneRecord]);

	const handleSelectContact = useCallback(
		(id) => () => {
			dispatch(contactTake.actions.selectContact(id));
		},
		[dispatch],
	);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore('take')(true));
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage('take')(page - 1));
		},
		[dispatch],
	);

	const handleReload = useCallback(() => {
		dispatch(reload('take'));
	}, [dispatch]);

	const hasRecords = !isEmpty(contacts);

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>
							<div>{getLabelMessage('contactTake:client')}</div>
							<div>{getLabelMessage('contactTake:personIn')}</div>
						</TableCell>
						<TableCell>
							<div>{getLabelMessage('contactTake:address')}</div>
							<div>{getLabelMessage('contactTake:campaign')}</div>
						</TableCell>
						<TableCell>{getLabelMessage('contactTake:policyNo')}</TableCell>
						<TableCell>{getLabelMessage('contactTake:amount')}</TableCell>
						<TableCell>
							<div>{getLabelMessage('contactTake:sellerIn')}</div>
							<div>{getLabelMessage('contactTake:contactState')}</div>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{!hasRecords ? (
						<TableHasNoResults fetchState={fetchState} />
					) : (
						contacts.map((contact) => (
							<TableRow
								key={contact.id}
								onClick={handleSelectContact(contact.id)}
								sx={{
									backgroundColor: selectedContactId === contact.id ? 'primary.shades.8p' : 'default',
								}}
							>
								<ContactsRow
									id={contact.id}
									contact={contact}
									solutionStates={solutionStates}
									idkRegionsCodelist={idkRegionsCodelist}
									idkAgenciesCodelist={idkAgenciesCodelist}
								/>
							</TableRow>
						))
					)}
				</TableBody>

				<PaginationRow
					fetchStorePath={fetchStorePath}
					colSpan={100}
					onLoadNext={handleLoadMore}
					onPaginationClick={handlePaginationClick}
				/>
			</Table>
		</ComponentLoader>
	);
};

ContactsTable.propTypes = {
	idkAgenciesCodelist: PropTypes.arrayOf(PropTypes.object),
	idkRegionsCodelist: PropTypes.arrayOf(PropTypes.object),
	solutionStates: PropTypes.arrayOf(PropTypes.object),
};

export default compose(
	codelistProvider(codelists.idkRegions, 'idkRegionsCodelist'),
	codelistProvider(codelists.idkAgencies, 'idkAgenciesCodelist'),
	codelistProvider(codelists.solutionState, 'solutionStates'),
)(ContactsTable);
